import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing.large} 0;
`;

const CarouselWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const CarouselTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease-in-out;
  gap: ${({ theme }) => theme.spacing.medium};
  will-change: transform;
`;

const ProductCard = styled(Link)`
  flex: 0 0 300px;
  background: ${({ theme }) => theme.colors.gradients.card};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.gold};
  border: ${({ theme }) => theme.borders.light};
  transition: all 0.3s ${({ theme }) => theme.animations.bounce};
  position: relative;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${({ theme }) => theme.shadows.strong};
    border: ${({ theme }) => theme.borders.gold};
    ${({ theme }) => theme.effects.shine}
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${ProductCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProductInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  text-align: center;
  background: ${({ theme }) => theme.effects.glassmorphism};
`;

const ProductName = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1.2rem;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

const ProductPrice = styled.p`
  color: ${({ theme }) => theme.colors.accent};
  font-weight: 600;
  font-size: 1.1rem;
  font-family: ${({ theme }) => theme.fonts.secondary};
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: ${({ theme }) => theme.colors.gradients.primary};
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  
  &:hover {
    background: ${({ theme }) => theme.colors.gradients.secondary};
  }

  &.prev {
    left: ${({ theme }) => theme.spacing.medium};
  }

  &.next {
    right: ${({ theme }) => theme.spacing.medium};
  }
`;

const ProductCarousel = ({ products }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const trackRef = useRef(null);
  const autoSlideRef = useRef(null);
  const cardWidthRef = useRef(0);

  useEffect(() => {
    if (trackRef.current?.firstChild) {
      cardWidthRef.current = trackRef.current.firstChild.getBoundingClientRect().width + 16; // card width + gap
    }
  }, [products]);

  const handlePrev = () => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : products.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev < products.length - 1 ? prev + 1 : 0));
  };

  useEffect(() => {
    if (trackRef.current) {
      const offset = currentIndex * -cardWidthRef.current;
      trackRef.current.style.transform = `translateX(${offset}px)`;
    }
  }, [currentIndex]);

  useEffect(() => {
    if (products.length > 1) {
      clearInterval(autoSlideRef.current);
      autoSlideRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev < products.length - 1 ? prev + 1 : 0));
      }, 5000);
    } else {
      clearInterval(autoSlideRef.current);
    }

    return () => clearInterval(autoSlideRef.current);
  }, [products.length]);

  const stopAutoSlide = () => clearInterval(autoSlideRef.current);
  const startAutoSlide = () => {
    if (products.length > 1) {
      autoSlideRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev < products.length - 1 ? prev + 1 : 0));
      }, 5000);
    }
  };

  if (products.length === 0) return null;

  return (
    <CarouselContainer onMouseEnter={stopAutoSlide} onMouseLeave={startAutoSlide}>
      {products.length > 1 && (
        <CarouselButton className="prev" onClick={handlePrev}>
          <ChevronLeft size={20} />
        </CarouselButton>
      )}
      <CarouselWrapper>
        <CarouselTrack ref={trackRef}>
          {products.map(product => (
            <ProductCard key={product.id} to={`/product/${product.id}`}>
              <ProductImage 
                src={product.images?.[0] || '/placeholder-jewelry.jpg'} 
                alt={product.name || 'Product image'} 
              />
              <ProductInfo>
                <ProductName>{product.name || 'Product Name'}</ProductName>
                <ProductPrice>₹{product.price || '0'}</ProductPrice>
              </ProductInfo>
            </ProductCard>
          ))}
        </CarouselTrack>
      </CarouselWrapper>
      {products.length > 1 && (
        <CarouselButton className="next" onClick={handleNext}>
          <ChevronRight size={20} />
        </CarouselButton>
      )}
    </CarouselContainer>
  );
};

export default ProductCarousel;
